import React from 'react';
import { motion } from 'framer-motion';

interface HealthScoreGaugeProps {
  score: number;
  grade: string;
  color: string;
}

export const HealthScoreGauge: React.FC<HealthScoreGaugeProps> = ({ score, grade, color }) => {
  const radius = 120;
  const strokeWidth = 20;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * 0.75; // 75% of circle (270 degrees)
  const strokeDashoffset = circumference * (1 - progress);

  // Calculate needle rotation (starts at 225deg, ends at 495deg for 270deg arc)
  const needleRotation = 225 + (progress * 270);

  return (
    <div className="relative flex flex-col items-center justify-center">
      <svg width="300" height="220" viewBox="0 0 300 220" className="overflow-visible">
        {/* Background arc */}
        <path
          d={`M 30 190 A ${radius} ${radius} 0 1 1 270 190`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored segments */}
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--destructive))" />
            <stop offset="25%" stopColor="hsl(var(--chart-5))" />
            <stop offset="50%" stopColor="hsl(var(--chart-4))" />
            <stop offset="75%" stopColor="hsl(var(--chart-3))" />
            <stop offset="100%" stopColor="hsl(var(--chart-2))" />
          </linearGradient>
        </defs>
        
        {/* Progress arc */}
        <motion.path
          d={`M 30 190 A ${radius} ${radius} 0 1 1 270 190`}
          fill="none"
          stroke="url(#gaugeGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 2, ease: "easeOut" }}
        />

        {/* Center circle */}
        <circle cx="150" cy="190" r="8" fill="hsl(var(--muted-foreground))" />

        {/* Animated needle */}
        <motion.line
          x1="150"
          y1="190"
          x2="150"
          y2="80"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          initial={{ rotate: 225 }}
          animate={{ rotate: needleRotation }}
          transition={{ duration: 2, ease: "easeOut" }}
          style={{ transformOrigin: '150px 190px' }}
        />
        
        {/* Needle circle */}
        <motion.circle
          cx="150"
          cy="190"
          r="12"
          fill={color}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.5, duration: 0.3 }}
        />
      </svg>

      {/* Score display */}
      <div className="absolute top-[120px] flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="text-6xl font-bold"
          style={{ color }}
        >
          {score}
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="text-xl font-semibold text-muted-foreground mt-1"
        >
          {grade}
        </motion.div>
      </div>

      {/* Scale markers */}
      <div className="absolute bottom-0 w-full flex justify-between px-4 text-xs text-muted-foreground">
        <span>0</span>
        <span>25</span>
        <span>50</span>
        <span>75</span>
        <span>100</span>
      </div>
    </div>
  );
};
